import Clarity from "@microsoft/clarity";
import { AxiosError } from "axios";
import { memo, useEffect, useMemo, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { ProfileProps } from "..";
import { LexIcon } from "../../../../assets/icons/lex";
import useIsScrolled from "../../../hooks/useIsScrolled";
import { ApiError, Jurisprudence } from "../../../models";
import { getJurisprudenceById } from "../../../services/get-jurisprudence-by-id";
import { getQueryValue } from "../../../store/selectors";
import {
  formatDate,
  getHighlightedExcerpts,
  handleClickNote,
  handleInternalSpaNavigation,
  highlightFullText,
  printContent,
  processDocumentText,
  sanitizeAllHtml,
  saveDocumentWithNotify,
} from "../../../utils";
import { Pagewrapper } from "../../components";
import { PDLTypography } from "../../components/dna";
import LexLoading from "../../components/global/_lexLoading";
import NotesModal, {
  NotesModalType,
} from "../../components/global/_notesModal";
import JurisprudenceDetailsHeaderActions from "./components/JurisprudenceDetailsHeaderActions";

const HtmlContent = memo<{ html: string }>(({ html }) => (
  <div className="content-text" dangerouslySetInnerHTML={{ __html: html }} />
));

const JurisprudenceDetails = (props: ProfileProps) => {
  const { id } = useParams();
  const [jurisprudence, setJurisprudence] = useState<Jurisprudence | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isSavingDocument, setIsSavingDocument] = useState(false);

  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteContent] = useState("");
  const [noteTitle] = useState("");
  const [modalType, setModalType] = useState<NotesModalType>("note");
  const [articleId, setArticleId] = useState("");
  const [articleUrl, setArticleUrl] = useState("");

  const navigate = useNavigate();

  const isScrolled = useIsScrolled();

  const dispatch = useDispatch();

  const query = useSelector(getQueryValue);

  const title = highlightFullText(jurisprudence?.titulo || "", query);
  const itemType = jurisprudence?.tipo;
  const isDocket = itemType && itemType === "Súmulas";

  const processedText = useMemo(() => {
    return processDocumentText({
      htmlText: `${
        title ? `<h2 class="jurisprudence-title"> ${title} </h2 >` : ""
      } ${jurisprudence?.texto}`,
    });
  }, [jurisprudence?.texto, title]);

  const contentHtml = useMemo(() => {
    if (!jurisprudence?.texto) return "";
    return query
      ? getHighlightedExcerpts(
          processedText,
          query,
          processedText.length,
          false,
        )
      : jurisprudence.texto;
  }, [jurisprudence?.texto, query, processedText]);

  const handlePrint = () => {
    if (jurisprudence)
      printContent({
        text: processedText,
        title: jurisprudence?.titulo,
      });
  };
  const handleOpenFindBar = () => {
    window.dispatchEvent(new CustomEvent("pdl:openFindBar"));
  };

  const handleSaveDocument = () => {
    if (jurisprudence)
      saveDocumentWithNotify({
        dispatch,
        setIsSavingDocument,
        documentoId: jurisprudence?._id,
        id: jurisprudence?.id,
        tipo: "Jurisprudencia",
        url: window.location.href,
      });
  };

  useEffect(() => {
    const handleGlobalClick = (event: any) => {
      const rawTarget = event.target;

      const target =
        rawTarget.closest?.("[data-nota-link], .nota-super, a") || rawTarget;

      if (showNoteModal) return;

      if (target.classList?.contains("nota-super")) {
        handleClickNote(event);
        return;
      }

      if (target.tagName?.toLowerCase() === "a") {
        const href = target.getAttribute("href") || "";

        const url = new URL(href, window.location.origin);

        const artIdFromQuery = url.searchParams.get("artId");

        const match = href.match(/(art\d+)/i);

        const articlePart = artIdFromQuery || match?.[1];

        if (articlePart) {
          event.preventDefault();

          let cleaned = href.replace(/^\/?legislacao\//i, "");
          cleaned = cleaned.split("#")[0];
          cleaned = cleaned.split("?")[0];

          if (!artIdFromQuery) {
            if (!cleaned.toLowerCase().endsWith(articlePart.toLowerCase())) {
              cleaned = cleaned.replace(/art\d+/i, "") + articlePart;
            }
          } else {
            cleaned = articlePart;
          }

          setArticleId(cleaned);
          setArticleUrl(href);
          setModalType("legislation");
          setShowNoteModal(true);

          return;
        } else {
          if (handleInternalSpaNavigation(href, event, navigate)) {
            return;
          }
        }
      }
    };

    document.addEventListener("click", handleGlobalClick);
    return () => document.removeEventListener("click", handleGlobalClick);
  }, [jurisprudence, showNoteModal]);

  useEffect(() => {
    const loadJurisprudence = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError("");

        const response = await getJurisprudenceById(id);

        if (response && response.texto) {
          response.texto = processDocumentText({ htmlText: response.texto });
        }

        setJurisprudence(response);
      } catch (err) {
        const error = err as AxiosError<ApiError>;
        const status = error?.response?.status;

        setError(
          status === 404
            ? "Estamos trabalhando para disponibilizá-lo em breve."
            : error?.response?.data?.message ||
                "Não foi possível encontrar o documento",
        );
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadJurisprudence();
    }
  }, [id]);

  useEffect(() => {
    if (!jurisprudence) return;

    Clarity.event("jurisprudence_view");

    Clarity.setTag("doc_type", "jurisprudencia");
    Clarity.setTag("doc_id", jurisprudence.id);
    Clarity.setTag("doc_name", jurisprudence.titulo);
  }, [jurisprudence]);

  if (loading) {
    return (
      <Pagewrapper {...props} showHeader headerClass="details-sticky">
        <Container>
          <Row>
            <Col>
              <div className="content-loading">
                <LexLoading customText="Buscando jurisprudência..." />
              </div>
            </Col>
          </Row>
        </Container>
      </Pagewrapper>
    );
  }

  if (error) {
    return (
      <Pagewrapper {...props} showHeader headerClass="details-sticky">
        <Container>
          <Row>
            <Col>
              <div className="error-section text-center">
                <LexIcon />
                <PDLTypography size="xl" variant="primary" weight="medium">
                  Este documento ainda não está disponível.
                </PDLTypography>
                <p className="text-muted">{error}</p>
                <p className="text-muted">
                  Se for urgente, fale com a gente. Teremos prazer em ajudar.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </Pagewrapper>
    );
  }

  return (
    <Pagewrapper {...props} showHeader headerClass="details-sticky">
      <Container>
        <Row>
          <Col>
            <JurisprudenceDetailsHeaderActions
              title={jurisprudence?.titulo || "Jurisprudência"}
              isScrolled={isScrolled}
              isSavingDocument={isSavingDocument}
              onOpenFindBar={handleOpenFindBar}
              onSaveDocument={handleSaveDocument}
              onPrint={handlePrint}
              docLink={jurisprudence?.link_integra}
              text={sanitizeAllHtml(jurisprudence?.texto)}
            />
            <div className="jurisprudence-info-box">
              <PDLTypography as="span" size="sm">
                Jurisprudência
              </PDLTypography>

              {jurisprudence?.categoria && (
                <>
                  &nbsp;&bull;&nbsp;
                  <PDLTypography as="span" size="sm">
                    {jurisprudence?.categoria}
                  </PDLTypography>
                </>
              )}

              {!!jurisprudence?.tribunal && (
                <>
                  &nbsp;&bull;&nbsp;
                  <PDLTypography as="span" size="sm">
                    {jurisprudence?.tribunal}
                  </PDLTypography>
                </>
              )}
              {!!jurisprudence?.data && (
                <>
                  &nbsp;&bull;&nbsp;
                  <PDLTypography as="span" size="sm">
                    {formatDate(jurisprudence?.data)}
                  </PDLTypography>
                </>
              )}

              {jurisprudence?.relator && jurisprudence?.relator?.[0] && (
                <>
                  &nbsp;&bull;&nbsp;
                  <PDLTypography as="span" size="sm">
                    {jurisprudence?.relator.join(" - ")}
                  </PDLTypography>
                </>
              )}
            </div>
            <div className="mt-3">
              <div className="content-box">
                {jurisprudence?.texto ? (
                  <HtmlContent html={contentHtml as string} />
                ) : (
                  <p className="text-muted">Conteúdo não disponível.</p>
                )}
              </div>
            </div>
          </Col>
        </Row>

        <NotesModal
          noteContent={noteContent}
          noteTitle={noteTitle}
          setShowNoteModal={setShowNoteModal}
          showNoteModal={showNoteModal}
          type={modalType}
          articleId={articleId}
          url={articleUrl}
        />
      </Container>
    </Pagewrapper>
  );
};

export default JurisprudenceDetails;
